import React, { useEffect } from 'react';
import { Flex, FormField } from '@workday/canvas-kit-react';
import { PrimaryButton, TertiaryButton } from '@workday/canvas-kit-react/button';
import { RESET_PASSWORD_PATH, ROTATE_CRED_PATH, VERIFY_WITH_AUTHENTICATOR_PATH } from '../../../react-router-paths';
import { autoId, CardView, formFieldErrorPropsV2 } from '@workday/upc-common';
import { ID_PASSWORD_BUTTON, ID_FORGOT_PASSWORD_LINK, ID_PASSWORD } from '../../../autoIds';
import styles from './LoginPasswordEntry.module.scss';
import { selectUserEmail } from '../../../slices/login.slice';
import { useForm } from 'react-hook-form';
import { ACCOUNT_DEFAULT_PASSWORD } from '../../../nx-environment-variables';
import { useSelector } from 'react-redux';
import { useLoginPasswordMutation } from '../../../slices/auth-api.slice';
import { LoginLink } from '../../../shared/login-link/login-link';
import { useHistory } from 'react-router-dom';
import { serverError } from '../../../utils/server-error';
import { useTranslation } from 'react-i18next';
import { PasswordTextInput } from '../../../shared/password-input/password-input';
import { PasswordFormState } from '../../../slices/types';
import { useErrorHandling } from '../../../hooks/useErrorHandling';

export function LoginPasswordEntry() {
    const email = useSelector(selectUserEmail);

    const { t } = useTranslation('auth');

    const { handleSubmit, register, formState, setError } = useForm<PasswordFormState>({
        defaultValues: {
            password: ACCOUNT_DEFAULT_PASSWORD || ''
        }
    });

    const [verifyPassword, loginPasswordMutation] = useLoginPasswordMutation();

    useErrorHandling(loginPasswordMutation.error, {
        inlineErrors: ['UPC-1007', 'UPC-1054'],
        onInlineError: error => setError('password', serverError(error))
    });

    const history = useHistory();

    useEffect(() => {
        if (loginPasswordMutation.isSuccess && loginPasswordMutation.data.authenticatorEnabled) {
            history.replace(VERIFY_WITH_AUTHENTICATOR_PATH);
        }

        if (loginPasswordMutation.isSuccess && loginPasswordMutation.data.rotateCred) {
            history.replace(ROTATE_CRED_PATH);
        }
    }, [loginPasswordMutation.data, history, loginPasswordMutation.isSuccess]);

    function onSubmit(values) {
        verifyPassword({ password: values.password });
    }

    function onResetPasswordClick() {
        history.replace(RESET_PASSWORD_PATH);
    }

    return (
        <div className={styles.loginPasswordEntry}>
            <h1 className={styles.title}>{t('LoginPasswordEntry.title')}</h1>
            <p className={styles.email}>{email}</p>
            <CardView className={styles.card}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormField
                        className={styles.formField}
                        grow={true}
                        label={t('LoginPasswordEntry.passwordFieldLabel')}
                        id='password'
                        {...formFieldErrorPropsV2(
                            formState.errors.password,
                            undefined,
                            t('Required.fieldErrorLabel', { ns: 'shared' })
                        )}>
                        <PasswordTextInput
                            {...autoId(ID_PASSWORD)}
                            autoFocus={true}
                            autoComplete='current-password'
                            disabled={loginPasswordMutation.isLoading}
                            placeholder={t('LoginPasswordEntry.passwordInputPlaceholder') || undefined}
                            {...register('password', {
                                required: t('Required.fieldGeneric', { ns: 'shared' })
                            })}
                        />
                    </FormField>
                    <PrimaryButton
                        {...autoId(ID_PASSWORD_BUTTON)}
                        className={styles.signInButton}
                        disabled={loginPasswordMutation.isLoading}
                        grow={true}
                        size='large'
                        type='submit'>
                        {t('LoginPasswordEntry.submitButton')}
                    </PrimaryButton>
                </form>
            </CardView>

            <Flex flexDirection={'column'} alignItems={'center'}>
                <TertiaryButton
                    {...autoId(ID_FORGOT_PASSWORD_LINK)}
                    className={styles.passwordResetButton}
                    size='large'
                    onClick={onResetPasswordClick}>
                    {t('LoginPasswordEntry.forgotPasswordLink')}
                </TertiaryButton>
                <LoginLink />
            </Flex>
        </div>
    );
}
